import { BigBashEvent, Game } from "@/types/types";
import SmallHelpers from "@/utils/SmallHelpers";
import { TemplateResult, html } from "lit";
import GameResultsCalculator from "./GameResultsCalculator";
import ScoreCalculator from "./ScoreCalculator";

class ChartPropsManager {
	private gameResultsCalculator: GameResultsCalculator;
	private scoreCalculator: ScoreCalculator;
	private smallHelpers: SmallHelpers;
	private barChartColors: string[];

	constructor() {
		this.gameResultsCalculator = new GameResultsCalculator();
		this.scoreCalculator = new ScoreCalculator();
		this.smallHelpers = new SmallHelpers();

		const root = getComputedStyle(document.documentElement);
		this.barChartColors = [
			root.getPropertyValue("--color-sky").trim(),
			root.getPropertyValue("--color-sand").trim(),
			root.getPropertyValue("--color-lemon").trim(),
			root.getPropertyValue("--color-mint").trim(),
			root.getPropertyValue("--color-lime").trim(),
			root.getPropertyValue("--color-pink").trim(),
		].filter(Boolean);
	}

	// ---------- BAR CHART APIs ----------

	public getGamePointsChart(currentEvent: BigBashEvent, currentGame: Game): TemplateResult {
		const gameResults = this.gameResultsCalculator.getGameResults(currentGame, currentEvent);
		if (gameResults.size === 0) {
			return html`<div class="content-section"><div>No game data available</div></div>`;
		}

		const { labels, values, colors } = this.buildGamePointsData(currentEvent, gameResults);
		const barChartProps = this.buildBarChartProps(labels, values, colors);

		return html`
			<div class="content-section">
				<bar-chart .barChartProps=${barChartProps}></bar-chart>
			</div>
		`;
	}

	public getTotalPointsChart(currentEvent: BigBashEvent): TemplateResult {
		const gameResults = this.scoreCalculator.calculateScores(currentEvent);
		if (gameResults.size === 0) {
			return html`<div class="content-section"><div>No total points data available</div></div>`;
		}

		const { labels, values, colors } = this.buildTotalPointsData(currentEvent, gameResults);
		const barChartProps = this.buildBarChartProps(labels, values, colors);

		return html`
			<div class="content-section">
				<bar-chart .barChartProps=${barChartProps}></bar-chart>
			</div>
		`;
	}

	// ---------- Data shaping ----------

	private buildGamePointsData(
		currentEvent: BigBashEvent,
		gameResults: Map<string, number>
	): { labels: string[]; values: number[]; colors: string[] } {
		const teamData: { label: string; value: number; color: string; teamNumber: number }[] = [];

		gameResults.forEach((points, teamId) => {
			const team = this.smallHelpers.getTeamByTeamId(currentEvent, teamId);
			if (team) {
				teamData.push({
					label: `#${team.team_number} ${team.name}`,
					value: points,
					color: team.color || '#ccc',
					teamNumber: team.team_number
				});
			}
		});

		teamData.sort((a, b) => a.teamNumber - b.teamNumber);

		return {
			labels: teamData.map(t => t.label),
			values: teamData.map(t => t.value),
			colors: teamData.map(t => t.color)
		};
	}

	private buildTotalPointsData(
		currentEvent: BigBashEvent,
		gameResults: Map<string, number>
	): { labels: string[]; values: number[]; colors: string[] } {
		const teamData: { label: string; value: number; color: string; teamNumber: number }[] = [];

		gameResults.forEach((points, teamId) => {
			const team = this.smallHelpers.getTeamByTeamId(currentEvent, teamId);
			if (team) {
				teamData.push({
					label: `#${team.team_number} ${team.name}`,
					value: points,
					color: team.color || '#ccc',
					teamNumber: team.team_number
				});
			}
		});

		teamData.sort((a, b) => a.teamNumber - b.teamNumber);

		return {
			labels: teamData.map(t => t.label),
			values: teamData.map(t => t.value),
			colors: teamData.map(t => t.color)
		};
	}

	private buildBarChartProps(labels: string[], values: number[], colors: string[]) {
		const data = this.buildBarChartData(labels, values, colors);
		const options = this.buildBaseBarOptions(Math.max(...values));
		return { labels: data.labels, datasets: data.datasets, options };
	}

	private buildBarChartData(labels: string[], values: number[], colors?: string[]) {
		return {
			labels,
			datasets: [
				{
					data: values,
					backgroundColor: (context: any) => {
						const index = context.dataIndex;

						const colorStr = colors
							? colors[index % colors.length]
							: this.barChartColors[index % this.barChartColors.length] || "#ccc";

						return this._returnBarChartColor(context, colorStr);
					},
				},
			],
		};
	}

	private _returnBarChartColor(context: any, color: string) {
		const ctx = context.chart.ctx;
		if (color.endsWith('-circles')) {
			const baseColor = color.replace('-circles', '');
			return this._addCirclesToBarChart(ctx, baseColor);
		} else if (color.endsWith('-stripes')) {
			const baseColor = color.replace('-stripes', '');
			return this._addStripesToBarChart(ctx, baseColor);
		} else if (color.endsWith('-crosshatch')) {
			const baseColor = color.replace('-crosshatch', '');
			return this._addCrosshatchToBarChart(ctx, baseColor);
		} else if (color.endsWith('-grid')) {
			const baseColor = color.replace('-grid', '');
			return this._addGridToBarChart(ctx, baseColor);
		} else {
			return color;
		}
	}

	private _addCirclesToBarChart(ctx: CanvasRenderingContext2D, color: string) {
		const patternCanvas = document.createElement('canvas');
		const patternCtx = patternCanvas.getContext('2d');
		patternCanvas.width = 24;
		patternCanvas.height = 24;

		patternCtx.fillStyle = color;
		patternCtx.fillRect(0, 0, 24, 24);

		patternCtx.fillStyle = 'white';

		const positions = [
			{ x: 6, y: 6 },
			{ x: 18, y: 18 },
			{ x: 18, y: -6 },
			{ x: -6, y: 18 }
		];

		positions.forEach(pos => {
			patternCtx.beginPath();
			patternCtx.arc(pos.x, pos.y, 3, 0, Math.PI * 2);
			patternCtx.fill();
		});

		return ctx.createPattern(patternCanvas, 'repeat');
	}

	private _addStripesToBarChart(ctx: CanvasRenderingContext2D, color: string) {
		const patternCanvas = document.createElement('canvas');
		const patternCtx = patternCanvas.getContext('2d');
		patternCanvas.width = 20;
		patternCanvas.height = 20;

		patternCtx.fillStyle = color;
		patternCtx.fillRect(0, 0, 20, 20);

		patternCtx.strokeStyle = 'white';
		patternCtx.lineWidth = 3;
		patternCtx.beginPath();
		patternCtx.moveTo(0, 0);
		patternCtx.lineTo(20, 20);
		patternCtx.moveTo(-5, 15);
		patternCtx.lineTo(5, 25);
		patternCtx.moveTo(15, -5);
		patternCtx.lineTo(25, 5);
		patternCtx.stroke();

		return ctx.createPattern(patternCanvas, 'repeat');
	}

	private _addCrosshatchToBarChart(ctx: CanvasRenderingContext2D, color: string) {
		const patternCanvas = document.createElement('canvas');
		const patternCtx = patternCanvas.getContext('2d');
		patternCanvas.width = 16;
		patternCanvas.height = 16;

		patternCtx.fillStyle = color;
		patternCtx.fillRect(0, 0, 16, 16);

		patternCtx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
		patternCtx.lineWidth = 1.5;
		patternCtx.beginPath();
		patternCtx.moveTo(0, 0); patternCtx.lineTo(16, 16);
		patternCtx.moveTo(-4, 12); patternCtx.lineTo(4, 20);
		patternCtx.moveTo(12, -4); patternCtx.lineTo(20, 4);
		patternCtx.moveTo(16, 0); patternCtx.lineTo(0, 16);
		patternCtx.moveTo(20, 12); patternCtx.lineTo(12, 20);
		patternCtx.moveTo(4, -4); patternCtx.lineTo(-4, 4);
		patternCtx.stroke();

		return ctx.createPattern(patternCanvas, 'repeat');
	}

	private _addGridToBarChart(ctx: CanvasRenderingContext2D, color: string) {
		const patternCanvas = document.createElement('canvas');
		const patternCtx = patternCanvas.getContext('2d');
		patternCanvas.width = 12;
		patternCanvas.height = 12;

		patternCtx.fillStyle = color;
		patternCtx.fillRect(0, 0, 12, 12);

		patternCtx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
		patternCtx.lineWidth = 1.5;
		patternCtx.beginPath();
		patternCtx.moveTo(6, 0); patternCtx.lineTo(6, 12);
		patternCtx.moveTo(0, 6); patternCtx.lineTo(12, 6);
		patternCtx.stroke();

		return ctx.createPattern(patternCanvas, 'repeat');
	}

	// ---------- Bar chart options ----------

	private createDelayedBouncingBars() {
		return {
			y: {
				from: (ctx) => ctx.chart.scales.y.getPixelForValue(0),
				duration: 800,
				delay: (ctx) => ctx.dataIndex * 1000,
				easing: 'easeOutBounce'
			},
		};
	}

	private buildBaseBarOptions(maxValue: number) {
		return {
			animation: {
				onComplete: this.drawNumbersOnBars(),
			},
			animations: this.createDelayedBouncingBars(),
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: { display: false },
			},
			scales: {
				y: {
					display: false,
					beginAtZero: true,
					suggestedMax: maxValue * 1.2,
				},
				x: {
					ticks: {
						font: {
							family: "Anton, sans-serif",
							size: 12,
							weight: "bold",
						},
					},
					grid: {
						drawOnChartArea: false,
						drawTicks: false,
					},
				},
			},
		};
	}

	private drawNumbersOnBars() {
		return (animation: any) => {
			const chart = animation.chart;
			const ctx = chart.ctx;

			ctx.save();
			ctx.font = '20px "Anton"';
			ctx.fillStyle = "#000";
			ctx.textAlign = "center";
			ctx.textBaseline = "bottom";

			chart.data.datasets.forEach((dataset: any, i: number) => {
				const meta = chart.getDatasetMeta(i);
				meta.data.forEach((bar: any, index: number) => {
					const value = dataset.data[index];
					ctx.fillText(value, bar.x, bar.y - 5);
				});
			});

			ctx.restore();
		};
	}
}

export default ChartPropsManager;